import React, { createContext, useContext, useReducer, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { councilApiClient } from '../services/councilApiClient';
import { addPending, clearResolved, dropStale, type PendingReply } from './pendingReplies';

// Eternal-spinner backstop tuning (SP-3a follow-up). MAX_AGE is generous — an
// LLM foreman turn legitimately runs long; SWEEP is the drop-resolution tick.
const PENDING_REPLY_MAX_AGE_MS = 120_000;
const PENDING_REPLY_SWEEP_MS = 15_000;

export interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant' | 'system' | 'error';
  timestamp: string;
  // ChatDAGRAGDAG C2.1 sp3 — DAG parentage already rides the wire
  // (backend GET /chat/.../messages returns the raw blackboard message
  // dicts, which carry parent_ids; the ChatMap already reads `m.parent_ids`).
  // Surfacing it on the chat Message type lets the left-panel tree-view
  // detect forks. Absent/[] on a linear spine.
  parent_ids?: string[];
  metadata?: Record<string, any>;
}

export interface ConversationState {
  id: string;
  messages: Message[];
  participants: {
    id: string;
    name: string;
    type: 'user' | 'persona' | 'agent' | 'team';
    metadata?: Record<string, any>;
  }[];
  status: 'idle' | 'loading' | 'error';
  error?: string;
  metadata?: Record<string, any>;
}

type ConversationAction =
  | { type: 'ADD_MESSAGE'; payload: Omit<Message, 'id' | 'timestamp'> }
  | { type: 'SET_MESSAGES'; payload: Message[] }
  | { type: 'SET_STATUS'; payload: ConversationState['status'] }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'SET_METADATA'; payload: Record<string, any> }
  | { type: 'RESET' };

/** F4 — small stable string hash for deterministic STEER fork ids
 *  (`fk:<M>:<hash(content)>`), so a reload+resubmit of the same steer collides on
 *  the server's fork_id dedup scan. Not cryptographic; same input → same key is all
 *  the dedup needs. Reroll uses a fresh uuid (two rerolls = two FRs, by design). */
function _djb2(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

interface ConversationContextType {
  state: ConversationState;
  sendMessage: (content: string, metadata?: Record<string, any>) => Promise<void>;
  // Reply-targeting §3 — spine-addressed reply (cursor on a companion spine).
  // Deliberately BYPASSES the legacy in-flight lockout: the route returns an
  // ACK (~1s), the resident's answer arrives async via TURN_ADDED reload, and
  // consecutive sends to one spine QUEUE (the backend row lock serializes).
  // P2: residentNamesByPersona maps persona_id → display name so the ack's
  // per-resident dispatched[] entries get named pending chrome.
  sendSpineReply: (content: string, spineId: string,
                   residentNamesByPersona: Record<string, string | null>) => Promise<void>;
  // §3 — mid-spine fork. Branch a new spine from target.fromMessageId. Steer sends
  // content (mints an envelope); reroll regenerates (content ignored). Same
  // queue-not-block ack semantics as sendSpineReply; answer via TURN_ADDED reload.
  sendFork: (content: string, target: { spineId: string; fromMessageId: string;
             mode: 'steer' | 'reroll'; residentName: string | null }) => Promise<void>;
  // Pendings held from ack → reload repaint (pill morph + camp re-arm).
  pendingReplies: PendingReply[];
  resetConversation: () => void;
  setMessages: (messages: Message[]) => void;
  // Re-runs adapter.getMessages(sessionId) and dispatches SET_MESSAGES.
  // Used after operator approves a council proposal — the new spine
  // extension lives in session.data but ConversationContext hasn't
  // refetched. Bypasses loadedSessionIdRef gate so it always fires.
  reloadMessages: () => Promise<void>;
  // Insert-handler registry — lets compose-affordance components (e.g.
  // ComposeMenu rendered in ComposePanel / WorkshopRow at the top of the
  // surface) request text insertion into the textarea without needing a direct
  // prop-drill from the textarea owner. The textarea owner
  // (ConversationInterface) registers its cursor-aware insertAtCursor on
  // mount; any caller of requestInsert() invokes the active handler.
  // Falls back to silent no-op when no textarea is mounted (e.g. during
  // page transitions). Cursor-aware behavior is preserved because the
  // handler still runs in the textarea owner's scope.
  registerInsertHandler: (handler: (text: string) => void) => () => void;
  requestInsert: (text: string) => void;
  // Textarea-activity notifier — mirrors the insertHandler pattern but fires
  // on any user keystroke in the active textarea (debounced ~50ms by the
  // textarea owner). Subscribers (e.g. WorkshopRow in v0.4 tactical patch) can
  // register a handler that closes overlays on type-to-resume so the user's
  // first keystroke always lands in the input. Returns an unregister fn.
  registerTextareaActivity: (handler: () => void) => () => void;
  notifyTextareaActivity: () => void;
}

const initialState: ConversationState = {
  id: '',
  messages: [],
  participants: [],
  status: 'idle',
  metadata: {}
};

const ConversationContext = createContext<ConversationContextType | undefined>(undefined);

function conversationReducer(state: ConversationState, action: ConversationAction): ConversationState {
  switch (action.type) {
    case 'ADD_MESSAGE':
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            id: uuidv4(),
            timestamp: new Date().toISOString(),
            ...action.payload
          }
        ]
      };
    case 'SET_MESSAGES':
      return {
        ...state,
        messages: action.payload
      };
    case 'SET_STATUS':
      return {
        ...state,
        status: action.payload,
        error: action.payload === 'error' ? state.error : undefined
      };
    case 'SET_ERROR':
      return {
        ...state,
        status: 'error',
        error: action.payload
      };
    case 'SET_METADATA':
      return {
        ...state,
        metadata: {
          ...state.metadata,
          ...action.payload
        }
      };
    case 'RESET':
      return {
        ...initialState,
        id: state.id
      };
    default:
      return state;
  }
}

export interface ConversationProviderProps {
  children: React.ReactNode;
  sessionId: string;
  adapter: ConversationAdapter;
  initialMessages?: Message[];
  participants?: ConversationState['participants'];
  metadata?: Record<string, any>;
}

// Adapter interface for different conversation backends
export interface ConversationAdapter {
  sendMessage: (sessionId: string, content: string, metadata?: Record<string, any>) => Promise<Message>;
  getMessages: (sessionId: string) => Promise<Message[]>;
  initialize?: (sessionId: string, metadata?: Record<string, any>) => Promise<void>;
}

export const ConversationProvider: React.FC<ConversationProviderProps> = ({
  children,
  sessionId,
  adapter,
  initialMessages = [],
  participants = [],
  metadata = {}
}) => {
  const [state, dispatch] = useReducer(conversationReducer, {
    ...initialState,
    id: sessionId,
    messages: initialMessages,
    participants,
    metadata
  });

  // Synchronous within-tick guard against double-sends. `state.status`
  // alone is not sufficient: two rapid-fire sendMessage calls in the
  // same microtask share the captured closure and both see status
  // 'idle' until React re-renders. The ref flips synchronously, so
  // the second call in the same tick sees the true in-flight value.
  const inFlightRef = useRef(false);

  // Track whether we've already loaded messages for the current
  // sessionId. Without this, the load effect re-fires whenever the
  // adapter ref changes (e.g., react-query refetches the persona on
  // tab focus, causing PersonaChat to mint a new adapter instance) —
  // each refire would call getMessages() and dispatch SET_MESSAGES
  // with whatever the backend has persisted SO FAR, which during a
  // long send is partial or empty. That wipes the user's optimistic
  // ADD_MESSAGE entries (user bubble, error notice, in-flight
  // assistant). One load per sessionId is the right semantic;
  // subsequent updates flow through ADD_MESSAGE / sendMessage.
  const loadedSessionIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (loadedSessionIdRef.current === sessionId) {
      // Already loaded for this session — re-render-driven adapter
      // changes must NOT retrigger getMessages.
      return;
    }
    // Reset on actual session change so navigation to a different
    // session re-fetches.
    loadedSessionIdRef.current = sessionId;

    const initializeConversation = async () => {
      if (adapter.initialize) {
        try {
          await adapter.initialize(sessionId, metadata);
        } catch (error) {
          console.error('Failed to initialize conversation:', error);
          dispatch({ type: 'SET_ERROR', payload: 'Failed to initialize conversation' });
        }
      }

      if (initialMessages.length === 0) {
        try {
          const messages = await adapter.getMessages(sessionId);
          dispatch({ type: 'SET_MESSAGES', payload: messages });
        } catch (error) {
          console.error('Failed to load messages:', error);
          dispatch({ type: 'SET_ERROR', payload: 'Failed to load messages' });
        }
      }
    };

    initializeConversation();
  }, [sessionId, adapter, initialMessages.length]);

  const sendMessage = async (content: string, metadata?: Record<string, any>) => {
    if (!content.trim()) return;

    // Context-level double-send guard — blocks programmatic callers
    // (retry buttons, SendTo, keyboard shortcuts) AND form handlers.
    // UI-level form gates are NOT sufficient on their own; a second
    // sendMessage dispatched while loading would create duplicate
    // user messages + racing adapter calls.
    if (inFlightRef.current || state.status === 'loading') {
      console.warn('[ConversationContext] sendMessage called while loading — ignored');
      return;
    }
    inFlightRef.current = true;

    // L1+ Fix 5 (2026-05-12) — Mint message_id ONCE in the frontend so
    // the optimistic user-message id matches the backend's canonical
    // user_message.id (which chat.py Fix 1 reads from metadata.message_id
    // when present, falling back to a fresh UUID when absent).
    //
    // Without this: optimistic message gets uuidv4() (the reducer's
    // auto-mint), backend mints a different UUID, /dag returns artifact
    // bound to the backend's id, state.messages has the client's id →
    // tile binding can't resolve parent in messagesToDag → falls to
    // orphan_tray (Fix 2 safety net) until page refresh re-fetches
    // canonical messages via getChatMessages.
    const messageId = uuidv4();
    const metadataWithMsgId = { ...(metadata ?? {}), message_id: messageId };

    // Add user message immediately — id matches the canonical backend id
    dispatch({
      type: 'ADD_MESSAGE',
      // Payload extends Omit<Message, 'id'|'timestamp'> with optional id
      // so we can pre-mint. The reducer's spread (`...action.payload`
      // after the auto-id) makes the override work at runtime.
      payload: ({
        id: messageId,
        content,
        role: 'user',
        metadata: metadataWithMsgId,
      } as any)
    });

    dispatch({ type: 'SET_STATUS', payload: 'loading' });

    try {
      // Send to backend with message_id pinned in metadata so
      // envelope.source_message_id == user_message.id == optimistic.id
      const response = await adapter.sendMessage(sessionId, content, metadataWithMsgId);

      // Add assistant response
      dispatch({
        type: 'ADD_MESSAGE',
        payload: response
      });

      dispatch({ type: 'SET_STATUS', payload: 'idle' });
    } catch (error) {
      console.error('Failed to send message:', error);

      // Add error message
      dispatch({
        type: 'ADD_MESSAGE',
        payload: {
          content: 'Sorry, there was an error processing your message. Please try again.',
          role: 'error',
          metadata: { error }
        }
      });

      dispatch({ type: 'SET_ERROR', payload: 'Failed to send message' });
    } finally {
      inFlightRef.current = false;
    }
  };

  // Reply-targeting §3 — see ConversationContextType.sendSpineReply.
  const [pendingReplies, setPendingReplies] = useState<PendingReply[]>([]);
  // Guards only the ack round-trip (~1s), NOT the resident's turn — replies
  // QUEUE: compose stays usable while a peer thinks (spec decision 7).
  // SP-3c (ex-C7 root-fix): PER-SPINE, keyed by spineId. Was a single global
  // boolean → an in-flight ack on any spine silently swallowed a send to any
  // OTHER spine (invisible until SP-3b's multi-panel canvas made it the norm).
  // Same-spine still guards double-submit; different spines are independent.
  const ackInFlightRef = useRef(new Set<string>());

  // Eternal-spinner backstop (SP-3a follow-up): the reload self-heal can't
  // drain a pending whose async turn died SILENTLY (no answer, no turn_failed).
  // Sweep on an interval so the "reading the trail…" pill can't spin forever.
  // Generous cap — a foreman turn legitimately takes a while.
  useEffect(() => {
    const id = setInterval(() => {
      setPendingReplies((prev) => dropStale(prev, Date.now(), PENDING_REPLY_MAX_AGE_MS));
    }, PENDING_REPLY_SWEEP_MS);
    return () => clearInterval(id);
  }, []);

  const sendSpineReply = async (
    content: string,
    spineId: string,
    residentNamesByPersona: Record<string, string | null>,
  ) => {
    if (!content.trim()) return;
    if (ackInFlightRef.current.has(spineId)) return;   // per-spine double-click guard on the ack only
    ackInFlightRef.current.add(spineId);
    const messageId = uuidv4();
    // Optimistic operator bubble — trail stamp arrives with the canonical
    // reload; parents are authored server-side at the trail tip.
    dispatch({
      type: 'ADD_MESSAGE',
      payload: ({ id: messageId, content, role: 'user',
                  metadata: { message_id: messageId, source: 'spine_reply' } } as any),
    });
    try {
      // Resilience (SP-3a follow-up): a hung/lost ack retries ONCE with the
      // SAME message_id — exactly-once-safe now that author_spine_reply dedups
      // on it (a landed-but-lost ack dedups; a truly-dead one processes fresh).
      // Only ack-transport failures (client timeout / network) retry; an HTTP
      // error means the server answered, so it falls straight through to catch.
      let ack;
      for (let attempt = 0; ; attempt++) {
        try {
          ack = await councilApiClient.sendSpineReply(sessionId, spineId, {
            message: content, message_id: messageId,
          });
          break;
        } catch (err: any) {
          const retriable = err?.name === 'CouncilTimeoutError'
            || err?.name === 'TypeError';   // fetch network failure
          if (retriable && attempt < 1) continue;
          throw err;
        }
      }
      // SP-3a ACK honesty: a present-but-empty `dispatched` (or the explicit
      // all_dispatch_failed flag) means NO resident was scheduled — arm ZERO
      // pendings and surface an inline error (the old `?.length ?` fallback
      // armed a null phantom pending here → an immortal "responding…" spinner).
      if (ack?.all_dispatch_failed || (ack?.dispatched && ack.dispatched.length === 0)) {
        dispatch({ type: 'ADD_MESSAGE', payload: {
          content: 'Couldn’t reach the foreman just now — please try again.',
          role: 'error', metadata: { ack } } });
      } else {
        // P2: one entry per dispatched resident — each camp re-arms, each
        // resolves on ITS resident's answer. A MISSING dispatched (pre-P2 ack
        // shape) degrades to the addressed spine (personaId null = any answer
        // at the envelope resolves); `??` so present-but-empty never lands here.
        const dispatched: { persona_id: string | null; spine_root_message_id: string }[] =
          ack?.dispatched ?? [{ persona_id: null, spine_root_message_id: spineId }];
        setPendingReplies((prev) => dispatched.reduce(
          (acc, d) => addPending(acc, {
            envelopeMessageId: messageId,
            spineRootMessageId: d.spine_root_message_id,
            personaId: d.persona_id,
            trailId: ack?.trail_id ?? null,
            residentName: d.persona_id
              ? (residentNamesByPersona[d.persona_id] ?? null) : null,
            sinceTs: Date.now(),
          }), prev));
      }
      // status stays 'idle' — the answer arrives via the TURN_ADDED reload
    } catch (error: any) {
      console.error('Spine reply failed:', error);
      // §5: NoLiveResidents gets SPECIFIC copy (CouncilApiError.body carries
      // the route's structured detail).
      const detail = error?.body?.detail ?? error?.detail;
      const noResidents = detail?.error === 'NoLiveResidents';
      dispatch({ type: 'ADD_MESSAGE', payload: {
        content: noResidents
          ? 'No one is at that camp anymore — they may have been dismissed. Pick another message to reply to.'
          : 'Sorry, that reply could not be delivered. Please try again.',
        role: 'error', metadata: { error } } });
      dispatch({ type: 'SET_ERROR', payload: 'Failed to send reply' });
    } finally {
      ackInFlightRef.current.delete(spineId);
    }
  };

  // §3 — mid-spine fork. Mirrors sendSpineReply (optimistic bubble for steer +
  // per-resident pending fan + queue-not-block ack); authors a fork-root server-side.
  const sendFork = async (
    content: string,
    target: { spineId: string; fromMessageId: string;
              mode: 'steer' | 'reroll'; residentName: string | null },
  ) => {
    const { spineId, fromMessageId, mode, residentName } = target;
    if (mode === 'steer' && !content.trim()) return;
    if (ackInFlightRef.current.has(spineId)) return;   // per-spine guard (shares the reply ref)
    ackInFlightRef.current.add(spineId);
    const messageId = uuidv4();
    // F4 — deterministic steer fork_id (reload-resubmit dedupe); reroll fresh.
    const forkId = mode === 'steer' ? `fk:${fromMessageId}:${_djb2(content)}` : uuidv4();
    if (mode === 'steer') {
      dispatch({
        type: 'ADD_MESSAGE',
        payload: ({ id: messageId, content, role: 'user',
                    metadata: { message_id: messageId, source: 'fork_steer' } } as any),
      });
    }
    try {
      const ack = await councilApiClient.sendFork(sessionId, spineId, {
        from_message_id: fromMessageId, mode, fork_id: forkId,
        message: mode === 'steer' ? content : undefined,
        message_id: mode === 'steer' ? messageId : undefined,
      });
      const d = ack?.dispatched?.[0];
      if (d) {
        setPendingReplies((prev) => addPending(prev, {
          envelopeMessageId: ack.message_id ?? ack.fork_root_id,
          spineRootMessageId: d.spine_root_message_id,   // FR — re-arms the new camp
          personaId: d.persona_id,
          trailId: ack.trail_id ?? null,
          residentName,
          sinceTs: Date.now(),
        }));
      }
      // status stays 'idle' — the new branch's answer arrives via TURN_ADDED reload
    } catch (error: any) {
      const detail = error?.body?.detail ?? error?.detail;
      const noResidents = detail?.error === 'NoLiveResidents';
      dispatch({ type: 'ADD_MESSAGE', payload: ({
        content: noResidents
          ? 'No one is at that camp anymore — pick another message to fork from.'
          : 'Sorry, that fork could not be created. Please try again.',
        role: 'error', metadata: { error } } as any) });
    } finally {
      ackInFlightRef.current.delete(spineId);
    }
  };

  const resetConversation = () => {
    dispatch({ type: 'RESET' });
  };

  const setMessages = (messages: Message[]) => {
    dispatch({ type: 'SET_MESSAGES', payload: messages });
  };

  const reloadMessages = async () => {
    try {
      const messages = await adapter.getMessages(sessionId);
      dispatch({ type: 'SET_MESSAGES', payload: messages });
      // Reply-targeting §3 — pending replies self-heal: drop entries whose
      // envelope now has a peer answer (or vanished) in the fresh canonical set.
      setPendingReplies((prev) => clearResolved(prev, messages as any[]));
    } catch (error) {
      console.error('Failed to reload messages:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to reload messages' });
    }
  };

  // Insert-handler registry. Stored in a ref so registration doesn't
  // trigger a context-value churn (which would re-render every consumer
  // on mount/unmount of the textarea). Returns an unregister function
  // for clean useEffect teardown.
  const insertHandlerRef = useRef<((text: string) => void) | null>(null);

  const registerInsertHandler = (handler: (text: string) => void) => {
    insertHandlerRef.current = handler;
    return () => {
      if (insertHandlerRef.current === handler) {
        insertHandlerRef.current = null;
      }
    };
  };

  const requestInsert = (text: string) => {
    insertHandlerRef.current?.(text);
  };

  // Textarea-activity registry. Same ref-based pattern as registerInsertHandler
  // so registration doesn't cause a context-value churn.
  const textareaActivityHandlerRef = useRef<(() => void) | null>(null);

  const registerTextareaActivity = (handler: () => void) => {
    textareaActivityHandlerRef.current = handler;
    return () => {
      if (textareaActivityHandlerRef.current === handler) {
        textareaActivityHandlerRef.current = null;
      }
    };
  };

  const notifyTextareaActivity = () => {
    textareaActivityHandlerRef.current?.();
  };

  return (
    <ConversationContext.Provider
      value={{
        state,
        sendMessage,
        sendSpineReply,
        sendFork,
        pendingReplies,
        resetConversation,
        setMessages,
        reloadMessages,
        registerInsertHandler,
        requestInsert,
        registerTextareaActivity,
        notifyTextareaActivity,
      }}
    >
      {children}
    </ConversationContext.Provider>
  );
};

export const useConversation = () => {
  const context = useContext(ConversationContext);
  if (context === undefined) {
    throw new Error('useConversation must be used within a ConversationProvider');
  }
  return context;
};
